import type {
  BookDetailDto,
  FeaturesDto,
  MetadataApplyInput,
  MetadataCandidateDto,
  SettingsDto,
} from "@shelflist/shared";
import { normalizeKey } from "@shelflist/shared";
import { queryOptions } from "@tanstack/react-query";
import { api, call } from "./api";

export const metadataKeys = {
  settings: ["admin", "settings"] as const,
  features: ["settings", "features"] as const,
};

export const instanceSettingsQuery = queryOptions({
  queryKey: metadataKeys.settings,
  queryFn: (): Promise<SettingsDto> => call(api.admin.settings.$get()),
});

/** Non-secret feature flags for signed-in users (hides "Find metadata" when lookups are off). */
export const featuresQuery = queryOptions({
  queryKey: metadataKeys.features,
  queryFn: (): Promise<FeaturesDto> => call(api.settings.features.$get()),
  staleTime: 30_000,
});

// --- diff -------------------------------------------------------------------------

export const DIFF_FIELDS = [
  "title",
  "subtitle",
  "authors",
  "description",
  "publisher",
  "publishedDate",
  "language",
  "tags",
  "identifiers",
  "cover",
] as const;
export type DiffField = (typeof DIFF_FIELDS)[number];

/** `unchanged`: nothing to apply (identical or no candidate value); `empty`: current is empty; `different`. */
export type DiffState = "unchanged" | "empty" | "different";

export interface DiffRow {
  field: DiffField;
  state: DiffState;
  /** Only `empty` rows are pre-selected. */
  defaultChecked: boolean;
}

const plain = (html: string) =>
  html
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const same = (a: string | null | undefined, b: string | null | undefined) =>
  (a ?? "").trim() === (b ?? "").trim();

function textState(
  current: string | null | undefined,
  candidate: string | null | undefined,
): DiffState {
  if (!candidate?.trim()) return "unchanged";
  if (!current?.trim()) return "empty";
  return same(current, candidate) ? "unchanged" : "different";
}

/** Identifiers of the candidate that are new for the book or have another value. */
export function changedIdentifiers(book: BookDetailDto, candidate: MetadataCandidateDto) {
  const current = new Map(book.identifiers.map((i) => [i.type, i.value] as const));
  return candidate.identifiers.filter((i) => current.get(i.type) !== i.value);
}

/** Candidate subjects the book does not have as tags yet (case-insensitive). */
export function newTags(book: BookDetailDto, candidate: MetadataCandidateDto): string[] {
  const have = new Set(book.tags.map((t) => t.name.toLowerCase()));
  return candidate.subjects.filter((subject) => !have.has(subject.toLowerCase()));
}

export function stateOf(
  field: DiffField,
  book: BookDetailDto,
  candidate: MetadataCandidateDto,
): DiffState {
  switch (field) {
    case "title":
      return textState(book.title, candidate.title);
    case "subtitle":
      return textState(book.subtitle, candidate.subtitle);
    case "publisher":
      return textState(book.publisher?.name, candidate.publisher);
    case "publishedDate":
      return textState(book.publishedDate, candidate.publishedDate);
    case "language":
      return textState(book.language, candidate.language);
    case "description": {
      if (!candidate.description) return "unchanged";
      if (!book.description?.trim()) return "empty";
      return plain(book.description) === plain(candidate.description) ? "unchanged" : "different";
    }
    case "authors": {
      if (candidate.authors.length === 0) return "unchanged";
      if (book.authors.length === 0) return "empty";
      const key = (names: string[]) => names.map(normalizeKey).join("|");
      return key(book.authors.map((a) => a.name)) === key(candidate.authors)
        ? "unchanged"
        : "different";
    }
    case "tags": {
      if (newTags(book, candidate).length === 0) return "unchanged";
      return book.tags.length === 0 ? "empty" : "different";
    }
    case "identifiers": {
      const changes = changedIdentifiers(book, candidate);
      if (changes.length === 0) return "unchanged";
      const existing = new Set(book.identifiers.map((i) => i.type));
      return changes.every((i) => !existing.has(i.type)) ? "empty" : "different";
    }
    case "cover":
      if (!candidate.hasCover) return "unchanged";
      return book.hasCover ? "different" : "empty";
  }
}

export function buildDiff(book: BookDetailDto, candidate: MetadataCandidateDto): DiffRow[] {
  return DIFF_FIELDS.map((field) => {
    const state = stateOf(field, book, candidate);
    return { field, state, defaultChecked: state === "empty" };
  });
}

/** The request body for the selected rows; only selected, applicable rows are sent. */
export function buildApplyBody(
  book: BookDetailDto,
  candidate: MetadataCandidateDto,
  selected: ReadonlySet<DiffField>,
  updatedAt: string,
): MetadataApplyInput {
  const fields: MetadataApplyInput["fields"] = {};
  const on = (field: DiffField) =>
    selected.has(field) && stateOf(field, book, candidate) !== "unchanged";
  if (on("title")) fields.title = candidate.title;
  if (on("subtitle")) fields.subtitle = candidate.subtitle;
  if (on("authors")) fields.authors = candidate.authors;
  if (on("description")) fields.description = candidate.description;
  if (on("publisher")) fields.publisher = candidate.publisher;
  if (on("publishedDate")) fields.publishedDate = candidate.publishedDate;
  if (on("language")) fields.language = candidate.language;
  if (on("tags")) fields.tags = newTags(book, candidate);
  if (on("identifiers")) fields.identifiers = changedIdentifiers(book, candidate);
  const coverRef = candidate.coverUrl?.split("/").pop();
  return {
    updatedAt,
    fields,
    ...(on("cover") && coverRef
      ? { cover: { provider: candidate.provider, ref: decodeURIComponent(coverRef) } }
      : {}),
  };
}

/** The search form prefill: isbn13, else isbn, else title and first author. */
export function initialQuery(book: BookDetailDto): { isbn: string; title: string; author: string } {
  const id = (type: string) => book.identifiers.find((i) => i.type === type)?.value;
  const isbn = id("isbn13") ?? id("isbn");
  if (isbn) return { isbn, title: "", author: "" };
  return { isbn: "", title: book.title, author: book.authors[0]?.name ?? "" };
}

/** Published dates (`YYYY`, `YYYY-MM`, `YYYY-MM-DD`) formatted for the active locale. */
export function formatPublished(value: string, locale: string): string {
  const [year, month, day] = value.split("-").map(Number);
  if (!year) return value;
  const options: Intl.DateTimeFormatOptions =
    day !== undefined
      ? { dateStyle: "medium", timeZone: "UTC" }
      : month !== undefined
        ? { year: "numeric", month: "long", timeZone: "UTC" }
        : { year: "numeric", timeZone: "UTC" };
  return new Intl.DateTimeFormat(locale, options).format(
    new Date(Date.UTC(year, (month ?? 1) - 1, day ?? 1)),
  );
}
